import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import type { SecurityOverall, SecurityOverviewRow } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { EmptyState, ErrorState, SkeletonTable, useFetch, useToast } from '../components/ui';
import { OverallBadge } from '../components/ResourceSecurity';

type Filter = 'all' | 'attention' | SecurityOverall | 'never';

const RANK: Record<string, number> = { critical: 0, error: 1, warn: 2, never: 3, ok: 4 };

/**
 * Every Linux host and MikroTik the security check can reach, worst first.
 * The per-resource detail lives on each resource's Security card.
 */
export function SecurityPage() {
  const { can } = useAuth();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('attention');
  const { data, loading, error, reload } = useFetch(() => api.get<SecurityOverviewRow[]>('/api/security'), [], { refreshMs: 60_000 });

  const rows = useMemo(() => {
    const all = [...(data ?? [])].sort(
      (a, b) =>
        RANK[a.overall ?? 'never']! - RANK[b.overall ?? 'never']! ||
        b.critical - a.critical ||
        b.high - a.high ||
        a.name.localeCompare(b.name),
    );
    if (filter === 'all') return all;
    if (filter === 'never') return all.filter((r) => !r.overall);
    if (filter === 'attention') return all.filter((r) => r.overall === 'critical' || r.overall === 'warn' || r.overall === 'error');
    return all.filter((r) => r.overall === filter);
  }, [data, filter]);

  const count = (pred: (r: SecurityOverviewRow) => boolean) => (data ?? []).filter(pred).length;

  const scan = async (row: SecurityOverviewRow) => {
    try {
      await api.post(`/api/security/resource/${row.resourceId}/scan`);
      toast(`Checking ${row.name} — the result appears on its page in a minute or two`, 'good');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'bad');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Security</h1>
          <div className="page-subtitle">
            Known vulnerabilities in installed software, signs of intrusion and login activity, for every server and MikroTik with a
            login. Checked automatically once a day.
          </div>
        </div>
      </div>

      <div className="row" style={{ gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {(
          [
            ['attention', `Needs attention (${count((r) => r.overall === 'critical' || r.overall === 'warn' || r.overall === 'error')})`],
            ['critical', `Critical (${count((r) => r.overall === 'critical')})`],
            ['warn', `Warnings (${count((r) => r.overall === 'warn')})`],
            ['error', `Check failed (${count((r) => r.overall === 'error')})`],
            ['never', `Not checked yet (${count((r) => !r.overall)})`],
            ['ok', `No issues (${count((r) => r.overall === 'ok')})`],
            ['all', `All (${data?.length ?? 0})`],
          ] as Array<[Filter, string]>
        ).map(([key, label]) => (
          <button key={key} className={`btn btn-sm${filter === key ? ' btn-primary' : ''}`} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading && !data && <SkeletonTable rows={6} cols={7} />}
        {error && <ErrorState error={error} onRetry={reload} />}
        {data && rows.length === 0 && (
          <EmptyState
            title={data.length === 0 ? 'Nothing to check' : 'Nothing in this view'}
            text={
              data.length === 0
                ? 'Security checks run on Linux servers, PBXs, ViciDial, MagnusBilling and MikroTiks.'
                : 'Pick another filter above.'
            }
          />
        )}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th data-align="right" style={{ width: 80 }}>
                    Critical
                  </th>
                  <th data-align="right" style={{ width: 70 }}>
                    High
                  </th>
                  <th data-align="right" style={{ width: 110 }} title="Known vulnerabilities with a fix published">
                    Fixable vulns
                  </th>
                  <th data-align="right" style={{ width: 110 }} title="Failed logins in the last 24 hours">
                    Failed logins
                  </th>
                  <th style={{ width: 120 }}>Checked</th>
                  {can('tools:run') && <th style={{ width: 60 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.resourceId}>
                    <td>
                      <Link to={`/resources/${r.resourceId}`}>{r.name}</Link>
                      <div className="subtle truncate" style={{ fontSize: 11 }} title={r.error ?? undefined}>
                        {r.error ?? r.os ?? (r.platform === 'routeros' ? 'MikroTik' : 'Linux')}
                      </div>
                    </td>
                    <td>
                      <OverallBadge overall={r.overall} />
                      {r.intrusion > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--itr-critical)' }}>intrusion signs</div>
                      )}
                    </td>
                    <td data-align="right" className="nums" style={{ color: r.critical ? 'var(--itr-critical)' : undefined }}>
                      {r.critical}
                    </td>
                    <td data-align="right" className="nums" style={{ color: r.high ? 'var(--itr-warning)' : undefined }}>
                      {r.high}
                    </td>
                    <td data-align="right" className="nums">
                      {r.fixable}
                    </td>
                    <td data-align="right" className="nums">
                      {r.failedLogins}
                    </td>
                    <td className="subtle">{r.scannedAt ? formatRelative(r.scannedAt) : 'never'}</td>
                    {can('tools:run') && (
                      <td>
                        <button className="btn btn-sm btn-icon" title="Scan now" aria-label={`Scan ${r.name} now`} onClick={() => void scan(r)}>
                          <Play size={12} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
